import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import type { Site, TimeEntry, TodayState } from '@shared/types'
import { ApiError, get, isOffline, patch, post } from '../lib/api'
import { STORE_CACHE, idb } from '../lib/idb'
import { SiteWatcher, getCurrentFix, locationPermission, onLocationGranted, type Fix } from '../lib/geo'
import { showLocalNotification } from '../lib/push'
import {
  enqueue, newClientId, startOutboxDrain, subscribeOutbox, type OutboxItem,
} from '../lib/outbox'
import { useSession } from './session'
import { useToast } from './toast'

export interface SiteChangePrompt {
  kind: 'arrived' | 'departed'
  /** The site the phone is standing in (null when they have left everything). */
  site: Site | null
  /** The site they are still clocked into, if any. */
  openSite: Site | null
  fix: Fix
  /** Server id of the recorded geo event, so we can note what they chose. */
  geoEventId: string | null
}

interface TodayApi {
  state: TodayState | null
  loading: boolean
  error: string | null
  /** Set while a clock action is in flight, for button spinners. */
  busy: boolean
  pending: OutboxItem[]
  online: boolean
  siteChange: SiteChangePrompt | null
  refresh: () => Promise<void>
  clockIn: (siteId: string) => Promise<void>
  clockOut: (opts?: { lunch?: boolean; scheduleId?: string | null }) => Promise<void>
  switchSite: (siteId: string) => Promise<void>
  resolveSiteChange: (resolution: 'clocked_in' | 'clocked_out' | 'switched' | 'dismissed') => void
}

const TodayContext = createContext<TodayApi | null>(null)

const TODAY_CACHE_KEY = 'today'

export function TodayProvider({ children }: { children: ReactNode }) {
  const { user, settings } = useSession()
  const toast = useToast()

  const [state, setState] = useState<TodayState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<OutboxItem[]>([])
  const [online, setOnline] = useState(navigator.onLine)
  const [siteChange, setSiteChange] = useState<SiteChangePrompt | null>(null)

  const watcherRef = useRef<SiteWatcher | null>(null)
  const stateRef = useRef<TodayState | null>(null)
  stateRef.current = state

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    if (!user) return
    try {
      const next = await get<TodayState>('/api/today')
      setState(next)
      setError(null)
      // Keep the last good day on the device. A crew member who opens the app
      // in a basement still needs to see their clock and their site list.
      void idb.put(STORE_CACHE, next, TODAY_CACHE_KEY).catch(() => undefined)
    } catch (err) {
      if (isOffline(err)) {
        setError(stateRef.current ? null : 'No signal. Showing your last update.')
      } else if (!(err instanceof ApiError && err.status === 401)) {
        setError(err instanceof Error ? err.message : 'Could not load your day')
      }
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      setState(null)
      setLoading(false)
      // Signing out must not leave another person's day on the phone.
      void idb.delete(STORE_CACHE, TODAY_CACHE_KEY).catch(() => undefined)
      return
    }
    let cancelled = false
    setLoading(true)

    // Paint from cache immediately, then reconcile with the server.
    void idb.get<TodayState>(STORE_CACHE, TODAY_CACHE_KEY)
      .then((cached) => {
        if (cached && !cancelled && cached.user.id === user.id) {
          setState((current) => current ?? cached)
        }
      })
      .catch(() => undefined)

    void refresh()
    return () => {
      cancelled = true
    }
  }, [user, refresh])

  // Keep the running clock honest without hammering the API.
  useEffect(() => {
    if (!user) return
    const id = setInterval(() => void refresh(), 120_000)
    return () => clearInterval(id)
  }, [user, refresh])

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  useEffect(() => subscribeOutbox(setPending), [])

  useEffect(() => {
    if (!user) return
    startOutboxDrain((result) => {
      toast.success(`Synced ${result.sent} queued ${result.sent === 1 ? 'item' : 'items'}`)
      void refresh()
    })
  }, [user, toast, refresh])

  // -------------------------------------------------------------------------
  // Clock actions
  // -------------------------------------------------------------------------

  /**
   * Every clock action follows the same shape: grab a fix if we can, try the
   * network, and fall back to the outbox with an optimistic local update so the
   * screen tells the truth about what the worker just did.
   */
  const runClockAction = useCallback(async (
    kind: 'clock-in' | 'clock-out' | 'switch',
    url: string,
    siteId: string | null,
    optimistic: (current: TodayState, occurredAt: string) => TodayState,
    extra: Record<string, unknown> = {},
  ) => {
    if (!user) return
    setBusy(true)
    const occurredAt = new Date().toISOString()
    const clientId = newClientId()

    let fix: Fix | null = null
    try {
      fix = await getCurrentFix({ timeout: 8000 })
    } catch {
      // A clock-in without GPS is still a clock-in. Admins see the blank stamp.
    }

    const payload = {
      siteId,
      clientId,
      occurredAt,
      lat: fix?.lat,
      lng: fix?.lng,
      accuracy: fix?.accuracy,
      ...extra,
    }

    try {
      await post(url, payload)
      await refresh()
    } catch (err) {
      if (isOffline(err)) {
        await enqueue({
          kind,
          url,
          method: 'POST',
          payload,
          files: [],
          summary: kind === 'clock-out'
            ? extra.reason === 'lunch' ? 'Clock out for lunch' : 'Clock out'
            : kind === 'switch' ? 'Site change' : 'Clock in',
        })
        setState((current) => (current ? optimistic(current, occurredAt) : current))
        toast.warn('Saved offline. It will sync when you get signal.')
      } else {
        throw err
      }
    } finally {
      setBusy(false)
    }
  }, [user, refresh, toast])

  const clockIn = useCallback(async (siteId: string) => {
    const site = stateRef.current?.sites.find((s) => s.id === siteId)
    await runClockAction('clock-in', '/api/time/clock-in', siteId, (current, occurredAt) => {
      const entry: TimeEntry = {
        id: `local-${occurredAt}`,
        userId: current.user.id,
        siteId,
        siteName: site?.name,
        clockInAt: occurredAt,
        clockOutAt: null,
        clockInLat: null,
        clockInLng: null,
        clockOutLat: null,
        clockOutLng: null,
        source: 'manual',
        editReason: null,
        durationMs: 0,
      }
      return { ...current, openEntry: entry, entries: [entry, ...current.entries] }
    })
  }, [runClockAction])

  /** `lunch` marks it as a lunch break, which queues the clock-back-in reminder. */
  const clockOut = useCallback(async (opts: { lunch?: boolean; scheduleId?: string | null } = {}) => {
    await runClockAction('clock-out', '/api/time/clock-out', null, (current, occurredAt) => {
      if (!current.openEntry) return current
      const closed: TimeEntry = {
        ...current.openEntry,
        clockOutAt: occurredAt,
        durationMs: Date.parse(occurredAt) - Date.parse(current.openEntry.clockInAt),
      }
      return {
        ...current,
        openEntry: null,
        entries: current.entries.map((e) => (e.id === closed.id ? closed : e)),
      }
    }, opts.lunch ? { reason: 'lunch', scheduleId: opts.scheduleId ?? undefined } : {})
  }, [runClockAction])

  const switchSite = useCallback(async (siteId: string) => {
    const site = stateRef.current?.sites.find((s) => s.id === siteId)
    await runClockAction('switch', '/api/time/switch', siteId, (current, occurredAt) => {
      const closed = current.openEntry
        ? {
            ...current.openEntry,
            clockOutAt: occurredAt,
            durationMs: Date.parse(occurredAt) - Date.parse(current.openEntry.clockInAt),
          }
        : null
      const entry: TimeEntry = {
        id: `local-${occurredAt}`,
        userId: current.user.id,
        siteId,
        siteName: site?.name,
        clockInAt: occurredAt,
        clockOutAt: null,
        clockInLat: null,
        clockInLng: null,
        clockOutLat: null,
        clockOutLng: null,
        source: 'auto',
        editReason: null,
        durationMs: 0,
      }
      return {
        ...current,
        openEntry: entry,
        entries: [entry, ...current.entries.map((e) => (closed && e.id === closed.id ? closed : e))],
      }
    })
  }, [runClockAction])

  // -------------------------------------------------------------------------
  // Site-change detection
  // -------------------------------------------------------------------------

  const recordGeoEvent = useCallback(async (
    kind: 'enter' | 'exit', site: Site | null, fix: Fix,
  ): Promise<string | null> => {
    const payload = {
      kind,
      siteId: site?.id ?? null,
      lat: fix.lat,
      lng: fix.lng,
      accuracy: fix.accuracy,
      occurredAt: new Date(fix.at).toISOString(),
      clientId: newClientId(),
    }
    try {
      const { id } = await post<{ id: string }>('/api/time/geo-event', payload)
      return id
    } catch {
      await enqueue({
        kind: 'geo-event',
        url: '/api/time/geo-event',
        method: 'POST',
        payload,
        files: [],
        summary: `${kind === 'enter' ? 'Arrived at' : 'Left'} ${site?.name ?? 'site'}`,
      })
      return null
    }
  }, [])

  // See lib/geo: start watching on open only if that cannot raise a dialog.
  const [watchAllowed, setWatchAllowed] = useState(false)
  useEffect(() => {
    let cancelled = false
    void locationPermission().then((permission) => {
      if (!cancelled && permission === 'granted') setWatchAllowed(true)
    })
    const unsubscribe = onLocationGranted(() => setWatchAllowed(true))
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!user || !state || !watchAllowed) return

    const watcher = watcherRef.current ?? new SiteWatcher(
      { minAccuracyM: settings.geo.minAccuracyM, dwellSeconds: settings.geo.dwellSeconds },
      (event) => {
        if (event.type === 'error' || event.type === 'fix') return

        const current = stateRef.current
        if (!current) return
        const openSite = current.openEntry
          ? current.sites.find((s) => s.id === current.openEntry!.siteId) ?? null
          : null

        if (event.type === 'arrived') {
          // Already clocked in here — nothing to ask.
          if (current.openEntry?.siteId === event.site.id) return

          void recordGeoEvent('enter', event.site, event.fix).then((geoEventId) => {
            setSiteChange({ kind: 'arrived', site: event.site, openSite, fix: event.fix, geoEventId })
          })

          if (document.visibilityState !== 'visible') {
            void showLocalNotification(
              current.openEntry ? 'You have moved sites' : `You are at ${event.site.name}`,
              {
                body: current.openEntry
                  ? `Clock out of ${openSite?.name ?? 'your old site'} and into ${event.site.name}.`
                  : `Clock in to ${event.site.name}.`,
                tag: 'site-change',
                requireInteraction: true,
                data: { url: '/' },
              },
            )
          }
          return
        }

        // Departed: only worth mentioning if they are still on the clock.
        if (!current.openEntry) return
        void recordGeoEvent('exit', openSite, event.fix).then((geoEventId) => {
          setSiteChange({ kind: 'departed', site: null, openSite, fix: event.fix, geoEventId })
        })

        if (document.visibilityState !== 'visible') {
          void showLocalNotification('Still clocked in', {
            body: `You have left ${openSite?.name ?? 'the site'} but are still on the clock.`,
            tag: 'site-change',
            requireInteraction: true,
            data: { url: '/' },
          })
        }
      },
    )

    watcherRef.current = watcher
    watcher.setSites(state.sites)
    watcher.setOptions({
      minAccuracyM: settings.geo.minAccuracyM,
      dwellSeconds: settings.geo.dwellSeconds,
    })
    watcher.start()

    return () => watcher.stop()
  }, [user, state, watchAllowed, settings.geo.minAccuracyM, settings.geo.dwellSeconds, recordGeoEvent])

  useEffect(() => {
    watcherRef.current?.primeConfirmedSite(state?.openEntry?.siteId ?? null)
  }, [state?.openEntry?.siteId])

  const resolveSiteChange = useCallback((
    resolution: 'clocked_in' | 'clocked_out' | 'switched' | 'dismissed',
  ) => {
    const prompt = siteChange
    setSiteChange(null)
    if (prompt?.geoEventId) {
      void patch(`/api/time/geo-event/${prompt.geoEventId}`, { resolution }).catch(() => undefined)
    }
  }, [siteChange])

  const value = useMemo<TodayApi>(() => ({
    state,
    loading,
    error,
    busy,
    pending,
    online,
    siteChange,
    refresh,
    clockIn,
    clockOut,
    switchSite,
    resolveSiteChange,
  }), [
    state, loading, error, busy, pending, online, siteChange,
    refresh, clockIn, clockOut, switchSite, resolveSiteChange,
  ])

  return <TodayContext.Provider value={value}>{children}</TodayContext.Provider>
}

export function useToday(): TodayApi {
  const ctx = useContext(TodayContext)
  if (!ctx) throw new Error('useToday must be used inside TodayProvider')
  return ctx
}
